import { Button } from "@dash/form";
import { useTheme } from "@emotion/react";
import { useClickOutside } from "@mantine/hooks";
import React, { useState } from "react";
import { MdDeleteOutline, MdMoreVert, MdOutlineFileDownload, MdPeopleOutline, MdTerminal } from "react-icons/md";

import { ExportFormat, exportFormats } from "./exportReport";

const formatLabels: Record<ExportFormat, string> = {
  html: "HTML",
  pdf: "PDF",
  csv: "CSV",
  ipynb: "Jupyter (.ipynb)",
};

type Props = {
  canEdit: boolean;
  assigneeCount: number;
  isDisabled: boolean;
  isDownloadDisabled: boolean;
  onEditNotebook?: () => void;
  onAssign: () => void;
  onDownload: (format: ExportFormat) => void;
  onRemove: () => void;
};

export const ReportActionsMenu: React.FC<Props> = (props) => {
  const theme = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const ref = useClickOutside(() => setIsOpen(false));

  const run = (action: () => void) => () => {
    setIsOpen(false);
    action();
  };

  return (
    <div ref={ref} css={{ position: "relative" }}>
      <Button
        variant="outline"
        icon={<MdMoreVert />}
        onClick={() => setIsOpen((open) => !open)}
        isDisabled={props.isDisabled}
      />

      {isOpen && (
        <div
          role="menu"
          css={{
            position: "absolute",
            top: "calc(100% + 0.375rem)",
            right: 0,
            zIndex: 1000,
            minWidth: "240px",
            padding: "0.375rem",
            background: "white",
            borderRadius: theme.mediumBorderRadius,
            border: "1px solid rgba(0, 0, 0, 0.08)",
            boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12)",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {props.canEdit && props.onEditNotebook && (
            <MenuItem icon={<MdTerminal />} onClick={run(props.onEditNotebook)}>
              Edytuj notebook
            </MenuItem>
          )}
          {props.canEdit && (
            <MenuItem icon={<MdPeopleOutline />} onClick={run(props.onAssign)} hint={String(props.assigneeCount)}>
              Dostęp
            </MenuItem>
          )}
          {props.canEdit && <Divider />}

          <SectionLabel icon={<MdOutlineFileDownload />}>Pobierz</SectionLabel>
          {exportFormats.map((format) => (
            <MenuItem
              key={format}
              onClick={run(() => props.onDownload(format))}
              isDisabled={props.isDownloadDisabled}
              isIndented
            >
              {formatLabels[format]}
            </MenuItem>
          ))}

          {props.canEdit && (
            <>
              <Divider />
              <MenuItem icon={<MdDeleteOutline />} onClick={run(props.onRemove)} isDanger>
                Usuń raport
              </MenuItem>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const MenuItem: React.FC<{
  icon?: React.ReactNode;
  hint?: string;
  onClick: () => void;
  isDisabled?: boolean;
  isDanger?: boolean;
  isIndented?: boolean;
  children: React.ReactNode;
}> = ({ icon, hint, onClick, isDisabled, isDanger, isIndented, children }) => {
  const theme = useTheme();

  return (
    <button
      role="menuitem"
      onClick={onClick}
      disabled={isDisabled}
      css={{
        display: "flex",
        alignItems: "center",
        gap: "0.625rem",
        width: "100%",
        padding: "0.5rem 0.625rem",
        paddingLeft: isIndented ? "2.25rem" : "0.625rem",
        border: "none",
        borderRadius: theme.smallBorderRadius,
        background: "none",
        fontSize: "0.875rem",
        textAlign: "left",
        color: isDanger ? theme.colors.danger : "#1d1c1a",
        cursor: "pointer",
        "&:hover:not(:disabled)": { background: isDanger ? "rgba(192, 57, 43, 0.08)" : theme.colors.primaryLight },
        "&:disabled": { opacity: 0.4, cursor: "not-allowed" },
      }}
    >
      {icon && (
        <span
          css={{
            display: "flex",
            fontSize: "1.125rem",
            color: isDanger ? theme.colors.danger : theme.colors.primary,
          }}
        >
          {icon}
        </span>
      )}
      <span css={{ flex: 1 }}>{children}</span>
      {hint && (
        <span
          css={{
            minWidth: "1.5rem",
            padding: "0 0.375rem",
            borderRadius: "999px",
            background: theme.colors.primaryLight,
            color: theme.colors.primary,
            fontSize: "0.75rem",
            fontWeight: 600,
            textAlign: "center",
          }}
        >
          {hint}
        </span>
      )}
    </button>
  );
};

const SectionLabel: React.FC<{ icon: React.ReactNode; children: React.ReactNode }> = ({ icon, children }) => {
  const theme = useTheme();

  return (
    <div
      css={{
        display: "flex",
        alignItems: "center",
        gap: "0.625rem",
        padding: "0.5rem 0.625rem 0.25rem",
        fontSize: "0.75rem",
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        color: theme.colors.secondaryText,
      }}
    >
      <span css={{ display: "flex", fontSize: "1.125rem", color: theme.colors.primary }}>{icon}</span>
      {children}
    </div>
  );
};

const Divider: React.FC = () => (
  <div css={{ height: "1px", margin: "0.375rem 0.25rem", background: "rgba(0, 0, 0, 0.08)" }} />
);
